import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useMutateEditAdminUser } from '../../hooks/useApiHooks';
import InputWithLabel from '../../../../Components/InputWithLabel';
import { ErrorToast, SuccessToast } from '../../../../Utils/SweetAlert';
import { EditUserValidationSchema, userInitialValues } from '../../constant';
import PassowrdField from '../../../../Components/PasswordField';
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import "../../newstyle.css";
import { useQuery } from '@tanstack/react-query';
import { GetSingleAdminUser } from '../../../../Api/adminUser';
import AdminProfileSelect from '../../../../Components/AdminProfileSelect';
import CustomSelect from '../../../../Utils/CustomSelect';
import { RoleAutoComplete } from '../../../../Api/role';
import SaveButton from '../../../../Components/SaveButton';
import { BackButton } from '../../../../Components';

const EditAdminUser = () => {
  const navigate = useNavigate();
  const formRef = useRef();
  const stateData = useLocation();
  const { id } = stateData?.state || '';

  const [roleOptions, setRoleOptions] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const editAdminUser = useMutateEditAdminUser();

  const { data, refetch } = useQuery({
    queryKey: ['get-single-user'],
    queryFn: () => GetSingleAdminUser({ Id: id }),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || 'Server Error.');
    },
  });

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (data) {
      if (formRef?.current?.values) {
        formRef?.current?.setValues({
          profilePic: data?.data?.profilePicUrl,
          name: data?.data?.name,
          email: data?.data?.email,
          password: "******",
          countryCode: data?.data?.countryCode,
          mobileNo: data?.data?.mobileNo ? `${data?.data?.countryCode} ${data?.data?.mobileNo}` : "",
          roleId: data?.data?.roleId || '',
          status: data?.data?.status,
        });
        if (data?.data?.roleId) {
          const roleName = data?.data?.roleId?.name || '';
          const roleValue = data?.data?.roleId?._id || data?.data?.roleId;
          if (roleName && roleValue) {
            setRoleOptions([{ label: roleName, value: roleValue }]);
          }
        }
      }
    }
  }, [data]);

  useEffect(() => {
    (async () => {
      try {
        setRoleLoading(true);
        const res = await RoleAutoComplete({ search: '' });
        const items = res?.data?.itemData || [];
        setRoleOptions(items.map((r) => ({ label: r?.name, value: r?._id })));
      } finally {
        setRoleLoading(false);
      }
    })();
  }, []);

  const fetchRoleOptions = async (search) => {
    try {
      setRoleLoading(true);
      const res = await RoleAutoComplete({ search });
      const items = res?.data?.itemData || [];
      setRoleOptions(items.map((r) => ({ label: r?.name, value: r?._id })));
    } finally {
      setRoleLoading(false);
    }
  };

  /**
   * Used to fetch last name from url
   * @param {string} url "Url of selected image file" 
   * @returns "Spliced file name from selected image file"
   */
  function getLastNameFromUrl(url) {
    const parts = url.split("/");
    return parts[parts.length - 1];
  }

  const handleSubmitEditAdminUser = (values) => {
    const countryCode = (values?.countryCode && !values?.countryCode?.includes("+")) ? `${"+"}${values?.countryCode}` : (values?.countryCode || "");
    let mobileNo = values?.mobileNo;
    if (mobileNo && countryCode) {
      if (mobileNo.includes(`${countryCode} `)) {
        mobileNo = mobileNo?.replace(`${countryCode} `, "");
      } else if (mobileNo.includes(`${countryCode}`)) {
        mobileNo = mobileNo?.replace(countryCode, "");
      } else if (values?.countryCode && mobileNo.includes(values?.countryCode)) {
        mobileNo = mobileNo.replace(values?.countryCode, "");
      }
    }
    const reqData = {
      id: id,
      profilePic: values?.profilePic ? getLastNameFromUrl(values?.profilePic) : "",
      name: values?.name,
      email: values?.email,
      countryCode: countryCode,
      mobileNo: mobileNo,
      roleId: values?.roleId,
      status: values?.status,
    };

    setIsLoading(true);
    editAdminUser.mutate(reqData, {
      onSuccess: (response) => {
        if (response?.status !== 0) {
          SuccessToast(response?.message || 'User Updated.');
          navigate('/admin/admin-user-listing');
        } else {
          ErrorToast(response?.message || 'Server Error.');
        }
        setIsLoading(false);
      },
      onError: (error) => {
        ErrorToast(error?.response?.data?.message || "Something Went Wrong");
        setIsLoading(false);
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>OVE | Admin Edit Admin</title>
      </Helmet>
      <div className='page-wrapper'>
        <div className='content container-fluid'>
          <div className='row'>
            <div className='col-md-12'>
              <div className='card card-border overflow-visible'>
                <div className='card-body'>
                  <div className='page-header d-block'>
                    <div className='row'>
                      <div className='col'>
                        <h3 className='page-title'>Edit Admin</h3>
                      </div>
                      <div className='col-auto d-flex align-items-center justify-content-center'>
                        <BackButton to="/admin/admin-user-listing" />
                      </div>
                    </div>
                  </div>
                  <section className='section-border'>
                    <Formik
                      initialValues={userInitialValues}
                      validationSchema={EditUserValidationSchema}
                      validateOnChange
                      onSubmit={handleSubmitEditAdminUser}
                      innerRef={formRef}
                    >
                      {(formik) => {

                        const { setFieldValue, values, isSubmitting } = formik;

                        return (
                          <Form>
                            <div className='row'>
                              <div className="col-md-3 d-flex justify-content-center">
                                <Field
                                  name="profilePic"
                                  component={AdminProfileSelect}
                                  require={false}
                                />
                              </div>
                              <div className="col-md-9 d-flex justify-content-between">
                                <div className='row gy-4 me-1'>
                                  <div className='col-md-6'>
                                    <Field
                                      Label='Name'
                                      type='text'
                                      name='name'
                                      placeholder='Enter User Name'
                                      component={InputWithLabel}
                                    />
                                  </div>
                                  <div className='col-md-6'>
                                    <Field
                                      Label='Email'
                                      type='text'
                                      name='email'
                                      placeholder='Enter Email'
                                      component={InputWithLabel}
                                    />
                                  </div>
                                  <div className='col-md-6'>
                                    <label className=''>
                                      Role<span className='text-danger'>*</span>
                                    </label>
                                    <CustomSelect
                                      name='roleId'
                                      placeholder='Select Role'
                                      options={roleOptions}
                                      isLoading={roleLoading}
                                      value={roleOptions.find((opt) => opt.value === values?.roleId) || null}
                                      onChange={(option) => setFieldValue('roleId', option ? option.value : '')}
                                      onInputChange={(inputValue) => {
                                        fetchRoleOptions(inputValue);
                                        return inputValue;
                                      }}
                                    />
                                    <ErrorMessage name='roleId' component='div' className='text-danger' />
                                  </div>
                                  <div className="col-md-6">
                                    <label>
                                      Mobile Number <span className='text-danger'>*</span>
                                    </label>
                                    <PhoneInput
                                      name="mobileNo"
                                      country={'gb'}
                                      countryCodeEditable={false}
                                      value={values?.mobileNo}
                                      onChange={(phone1, phone2, phone3) => {
                                        setFieldValue("mobileNo", phone1);
                                        setFieldValue("countryCode", phone2.dialCode);
                                      }}
                                      inputStyle={{
                                        background: "#F7F5F5",
                                        height: "38px",
                                        boxShadow: "none",
                                        borderRadius: "8px",
                                        width: "100%"
                                      }}
                                    />
                                    <ErrorMessage name="mobileNo" component='div' className='text-danger' />
                                  </div>
                                  <div className='col-md-6'>
                                    <label className=''>
                                      Password <span className='text-danger'>*</span>
                                    </label>

                                    <div className='disabled-field'>
                                      <Field className="disabled" component={PassowrdField} value={values?.password} disabled={true} name='password' />
                                    </div>

                                  </div>
                                </div>
                              </div>
                            </div>

                            <SaveButton loading={isLoading} />
                          </Form>
                        );
                      }}
                    </Formik>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditAdminUser;